<template>
  <div id="app">
    <template v-if="showBlockedState">
      <div class="min-h-screen flex items-center justify-center bg-white text-black px-4">
        <div class="text-center">
          <h1 class="text-2xl sm:text-3xl font-bold">
            You must be 21 or older to enter
          </h1>
          <div class="mt-6 flex flex-col sm:flex-row justify-center items-center gap-4">
            <a
              href="/PrivacyPolicy.html"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-semibold underline"
            >
              Privacy Policy
            </a>
            <a
              href="/TermsOfUse.html"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-semibold underline"
            >
              Terms of Use
            </a>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <WebHeader />
      <router-view></router-view>
    </template>

    <div
      v-if="showAgeGateModal"
      class="age-gate-overlay fixed inset-0 z-[9999] w-screen h-[100dvh] bg-black/45 backdrop-blur-md flex items-center justify-center pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] px-4 sm:px-6 overscroll-none"
      @keydown.tab.prevent="trapFocus"
      @keydown.esc.prevent
    >
      <div
        ref="ageGateDialog"
        class="w-full max-w-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        aria-describedby="age-gate-description"
        tabindex="-1"
      >
        <h1 id="age-gate-title" class="text-2xl sm:text-3xl font-bold">
          Are you 21 years of age or older?
        </h1>
        <p
          id="age-gate-description"
          class="mt-3 text-sm sm:text-base text-gray-700 dark:text-gray-300"
        >
          By entering, you confirm you are at least 21 and agree to our
          <a
            href="/TermsOfUse.html"
            target="_blank"
            rel="noopener noreferrer"
            class="underline font-semibold"
          >
            Terms of Use
          </a>
          and
          <a
            href="/PrivacyPolicy.html"
            target="_blank"
            rel="noopener noreferrer"
            class="underline font-semibold"
          >
            Privacy Policy
          </a>.
        </p>

        <div class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            @click="acceptAgeGate"
            class="px-5 py-3 rounded-lg bg-yellow-500 text-gray-900 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
          >
            Yes, I am 21+
          </button>
          <button
            type="button"
            @click="declineAgeGate"
            class="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
          >
            No, I am under 21
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WebHeader from "./components/WebHeader.vue";
import { useDarkModeStore } from "@/stores/darkMode";
import { getItem, removeItem, setItem } from "@/utils/storage";

const AGE_GATE_STORAGE_KEY = "age_gate_v1";
const AGE_GATE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export default {
  name: "App",
  components: { WebHeader },
  data() {
    return {
      ageVerified: false,
      isAgeDenied: false,
      lockedScrollY: 0,
    };
  },
  setup() {
    const darkModeStore = useDarkModeStore();
    return { darkModeStore };
  },
  computed: {
    isLandingRoute() {
      const routeName = this.$route?.name;
      const routePath = this.$route?.path;
      return routeName === "LandingPage" || routePath === "/";
    },
    showBlockedState() {
      if (!this.isLandingRoute) return false;
      return this.isAgeDenied;
    },
    showAgeGateModal() {
      if (!this.isLandingRoute) return false;
      return !this.ageVerified && !this.isAgeDenied;
    },
  },
  mounted() {
    this.darkModeStore.init();
    this.initializeAgeGate();
    this.focusAgeGateDialog();
  },
  beforeUnmount() {
    this.setModalScrollLock(false);
  },
  updated() {
    if (this.showAgeGateModal) {
      this.focusAgeGateDialog();
    }
  },
  watch: {
    showAgeGateModal: {
      immediate: true,
      handler(isOpen) {
        this.setModalScrollLock(isOpen);
      },
    },
  },
  methods: {
    initializeAgeGate() {
      const raw = getItem(AGE_GATE_STORAGE_KEY, "");
      if (!raw) return;

      try {
        const parsed = JSON.parse(raw);
        const isValidAcceptedState =
          parsed?.accepted === true &&
          typeof parsed?.expiresAt === "number" &&
          parsed.expiresAt > Date.now();

        if (isValidAcceptedState) {
          this.ageVerified = true;
          return;
        }
      } catch (error) {
        console.warn("Invalid age gate state:", error?.message || error);
      }

      removeItem(AGE_GATE_STORAGE_KEY);
    },
    focusAgeGateDialog() {
      this.$nextTick(() => {
        if (this.$refs.ageGateDialog && this.$refs.ageGateDialog.focus) {
          this.$refs.ageGateDialog.focus();
        }
      });
    },
    trapFocus(event) {
      if (!this.$refs.ageGateDialog) return;

      const focusable = this.$refs.ageGateDialog.querySelectorAll(
        'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        last.focus();
        return;
      }

      if (!event.shiftKey && active === last) {
        first.focus();
      }
    },
    acceptAgeGate() {
      const state = {
        accepted: true,
        expiresAt: Date.now() + AGE_GATE_MAX_AGE_MS,
      };

      setItem(AGE_GATE_STORAGE_KEY, JSON.stringify(state));
      this.ageVerified = true;
      this.isAgeDenied = false;
      this.trackAgeGateEvent("age_gate_accept");
    },
    declineAgeGate() {
      this.ageVerified = false;
      this.isAgeDenied = true;
      this.trackAgeGateEvent("age_gate_decline");
    },
    setModalScrollLock(isLocked) {
      if (isLocked) {
        this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.lockedScrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
        return;
      }

      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";

      if (this.lockedScrollY) {
        window.scrollTo(0, this.lockedScrollY);
        this.lockedScrollY = 0;
      }
    },
    // Hook placeholder for future analytics wiring.
    trackAgeGateEvent(eventName, metadata = {}) {
      void eventName;
      void metadata;
    },
  },
};
</script>

<style scoped>
.age-gate-overlay {
  min-height: 100vh;
  min-height: 100dvh;
  height: -webkit-fill-available;
  -webkit-backdrop-filter: blur(12px);
}
</style>
